import emailjs from '@emailjs/browser';

import { ActivityLog, Tender, TenderStatus, AppSettings } from '../types';

import { apiService } from './apiService';
import { settingsService } from './settingsService';

// Migrated to DB: use API instead of localStorage
export const getLogs = async (): Promise<ActivityLog[]> => {
  try {
    const logs = await apiService.getAuditLogs(undefined, 500);
    return logs.map((l: any) => ({
      id: l.id,
      timestamp: l.timestamp,
      action: l.action,
      details: l.details,
      user: l.userName || l.user?.name || 'Usuário',
      type: l.type
    }));
  } catch (error) {
    console.error('Erro ao carregar logs de auditoria:', error);
    return [];
  }
};

export const logActivity = async (
  action: string,
  details: string,
  type: 'INFO' | 'WARNING' | 'SUCCESS' | 'ERROR' = 'INFO',
  user: string = 'Usuário Atual'
) => {
  try {
    await apiService.createAuditLog({
      action,
      details,
      type,
      userName: user,
      // TODO: adicionar userId quando implementar autenticação
      userId: undefined
    });
  } catch (error) {
    console.error('Erro ao salvar log de auditoria:', error);
    // Fallback silencioso - não quebra a aplicação se o log falhar
  }
};

// Helper para ler configurações (seguro)
const getSettings = async (): Promise<AppSettings | null> => {
    try {
        return await settingsService.getAppSettings();
    } catch {
        return null;
    }
};

/**
 * Envia um e-mail transacional (Real via EmailJS ou Simulado).
 * Retorna true se o email foi enviado com sucesso.
 */
export const sendEmailNotification = async (
  tender: Tender,
  newStatus: TenderStatus,
  recipients: string = 'equipe-licitacoes@empresa.com'
): Promise<boolean> => {

  // Carregar preferências
  const settings = await getSettings();
  // Preferir a configuração específica de notificationPreferences.statusChange (nova estrutura),
  // senão cair para a flag legada notifyStatusChange no settings do backend.
  const shouldNotifyStatus = settings?.notificationPreferences?.statusChange ?? settings?.notifyStatusChange ?? true; // Default true se não configurado

  // Status que disparam notificação (Regra de Negócio)
  const criticalStatuses = [
    TenderStatus.PARTICIPATE,
    TenderStatus.HIGH_PRIORITY,
    TenderStatus.EXTREME_PRIORITY,
    TenderStatus.WON
  ];

  // Verifica se deve enviar: Se o status é crítico E a configuração permite
  if (criticalStatuses.includes(newStatus) && shouldNotifyStatus) {

    // Usar os emails configurados no settings se disponíveis
    const finalRecipients = settings?.emailRecipients || recipients;

    // Tentar envio REAL se configurado
    if (settings?.emailJsConfig?.serviceId && settings?.emailJsConfig?.templateId && settings?.emailJsConfig?.publicKey) {
        try {
            const templateParams = {
                to_email: finalRecipients,
                title: tender.title,
                status: newStatus,
                organ: tender.organ,
                deadline: new Date(tender.deadline).toLocaleDateString('pt-BR'),
                value: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(tender.value),
                message: `O status da licitação ID ${tender.id} foi alterado para ${newStatus}. Favor verificar a plataforma.`
            };

            await emailjs.send(
                settings.emailJsConfig.serviceId,
                settings.emailJsConfig.templateId,
                templateParams,
                settings.emailJsConfig.publicKey
            );

            await logActivity(
                'NOTIFICACAO_EMAIL_REAL',
                `E-mail enviado via EmailJS para "${finalRecipients}" (Status: ${newStatus})`,
                'SUCCESS'
            );
            return true;

        } catch (error: any) {
            console.error('Erro ao enviar EmailJS:', error);
            await logActivity(
                'ERRO_EMAIL',
                `Falha ao enviar via EmailJS: ${error.text || error.message}`,
                'ERROR'
            );
            // Fallback para log simulado abaixo se falhar
        }
    }

    // --- FALLBACK: Simulação (Se não configurado ou se falhar) ---

    await new Promise(resolve => setTimeout(resolve, 1500));

    console.group('📧 [MOCK EMAIL SERVICE]');
    console.log(`To: ${finalRecipients}`);
    console.log(`Subject: 🚨 AÇÃO NECESSÁRIA: ${newStatus.toUpperCase()} - ${tender.title}`);
    console.log(`Body: A licitação ID ${tender.id} (${tender.organ}) teve seu status alterado para ${newStatus}.`);
    console.groupEnd();

    // Se falhou o EmailJS mas passou aqui, registramos como sucesso simulado (apenas se não houver erro prévio)
    // Mas para manter simples, o componente UI mostrará sucesso se retornar true.

    if (!settings?.emailJsConfig?.serviceId) {
        await logActivity(
        'NOTIFICACAO_EMAIL_SIMULADA',
        `E-mail (Simulado) para "${finalRecipients}" - Configurar EmailJS para envio real.`,
        'INFO'
        );
    }

    return true;
  }
  return false;
};
